// Phase NAATI-MAX B2 — /translate/naati/ccl-test-prep
import { createFileRoute } from "@tanstack/react-router";
import NaatiKnowledgePage from "@/components/NaatiKnowledgePage";
import { CCL_FACTS_TH, CCL_PREP_PACK_TH } from "@/data/naati-ccl";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/translate/naati/ccl-test-prep`;

export const Route = createFileRoute("/translate/naati/ccl-test-prep")({
  head: () => ({
    meta: [
      { title: ctaTitle("ติว NAATI CCL Test ไทย-อังกฤษ +5 แต้ม | NYC", "/translate/naati/ccl-test-prep", "th")},
      { name: "description", content: ctaDesc("ติว NAATI CCL Test ไทย-อังกฤษ 50 ชั่วโมง · 30 mock dialogues (Healthcare/Legal/Centrelink) — เป้าหมาย +5 แต้ม SkillSelect ทักไลน์ประเมินระดับฟรีก่อนสมัครสอบ", "/translate/naati/ccl-test-prep", "th")},
      { property: "og:title", content: ctaTitle("ติว NAATI CCL Test ไทย-อังกฤษ +5 แต้ม | NYC", "/translate/naati/ccl-test-prep", "th")},
      { property: "og:description", content: ctaDesc("ติวสอบ CCL 50 ชั่วโมง 30 mock dialogues — เป้าหมาย +5 แต้ม SkillSelect ทักไลน์ประเมินระดับฟรีวันนี้", "/translate/naati/ccl-test-prep", "th")},
      { property: "og:url", content: URL },
    ],
    links: canonicalAndHreflang("/translate/naati/ccl-test-prep", "/en/translate/naati/ccl-test-prep"),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Course",
        name: "ติวสอบ NAATI CCL Test (Thai-English)",
        description: "คอร์สเตรียมสอบ CCL 50 ชั่วโมง สำหรับ +5 แต้ม Skilled Migration",
        provider: { "@type": "Organization", name: "NYC Language Institute", sameAs: SITE },
        inLanguage: "th",
        url: URL,
        offers: {
          "@type": "Offer", price: CCL_PREP_PACK_TH.priceThb, priceCurrency: "THB",
          availability: "https://schema.org/InStock",
        },
        hasCourseInstance: {
          "@type": "CourseInstance",
          courseMode: "Online",
          courseWorkload: `PT${CCL_PREP_PACK_TH.hoursRecommended}H`,
        },
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "th",
        mainEntity: CCL_FACTS_TH.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage", "@id": `${URL}#speakable`, url: URL,
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
      }) },
    ],
  }),
  component: () => (
    <NaatiKnowledgePage
      locale="th"
      eyebrow="CCL Test Prep · Bonus 5 Points"
      h1="ติวสอบ NAATI CCL ไทย-อังกฤษ เพิ่ม 5 แต้ม Skilled Migration"
      lead="คอร์สเตรียมสอบ NAATI CCL (Credentialed Community Language) สำหรับผู้ยื่นวีซ่า Skilled Independent 189 / Nominated 190 / Regional 491 — 50 ชั่วโมง 30 mock dialogues ครอบคลุมโดเมนจริงของ NAATI ทั้งหมด."
      speakableAnswer={`NAATI CCL Test คือการแปลบทสนทนา 2 ตอน (TH↔EN) ตอนละ 300 คำ ใช้คะแนน 5 แต้มในระบบ SkillSelect ของ Department of Home Affairs. คอร์สเรา 50 ชั่วโมง ราคา ฿${CCL_PREP_PACK_TH.priceThb.toLocaleString()}.`}
      sections={[
        {
          heading: "สิ่งที่คุณจะได้รับในแพ็ก",
          bullets: CCL_PREP_PACK_TH.inclusionsTh,
        },
        {
          heading: "โดเมนที่ครอบคลุมในคอร์ส",
          paragraph: "NAATI ออกข้อสอบจริงครอบคลุม 7 โดเมนหลัก — คอร์สเราเตรียมโจทย์ทั้งหมด.",
          bullets: CCL_PREP_PACK_TH.domainsCovered.map((d) => `${d} — mock dialogues ตามแนว NAATI`),
        },
        {
          heading: "ใครเหมาะกับคอร์สนี้?",
          bullets: [
            "ผู้ยื่น EOI วีซ่า 189 / 190 / 491 ที่ต้องการคะแนนเพิ่ม 5 แต้ม",
            "ผู้ที่มีพื้นภาษาอังกฤษระดับ IELTS 6.5+ หรือ PTE 65+",
            "ผู้ที่ต้องการสอบในรอบ 6-12 เดือนข้างหน้า",
            "ผู้ที่เคยสอบไม่ผ่านและต้องการสอบใหม่ (Re-sit)",
          ],
        },
        {
          heading: "เปรียบเทียบกับ NAATI Translation",
          paragraph: "NAATI CCL Test ≠ NAATI Translation Service. CCL เป็นการสอบเพื่อรับคะแนนเพิ่ม Skilled Migration. NAATI Translation คือบริการแปลเอกสารพร้อมตราประทับสำหรับยื่นวีซ่า. ทั้งสองเสริมกัน — ผู้สอบ CCL ผ่านแล้ว ยังต้องแปล Transcript / Police Clearance / Marriage Certificate ด้วย NAATI Stamp อยู่ดี.",
        },
      ]}
      faqs={CCL_FACTS_TH}
      cta={{ label: "ลงทะเบียนติวสอบ CCL", href: "/translate/naati" }}
    />
  ),
});
